
import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { 
  TableCell, 
  TableRow 
} from '@/components/ui/table';
import { 
  MapPin, 
  CalendarDays, 
  Clock, 
  User, 
  FileText,
  Pencil
} from 'lucide-react';
import { ItineraryEvent } from '@/types/itinerary';
import { TransportationIcon } from './TransportationIcon';
import { getConfirmationNumber, getCleanDescription, getUserName } from './TransportationUtils';
import { formatTimeTo12Hour } from '@/utils/dateUtils';
import { Button } from '@/components/ui/button';
import EditTransportationDialog from './EditTransportationDialog';

interface TransportationTableRowProps {
  event: ItineraryEvent;
}

export const TransportationTableRow: React.FC<TransportationTableRowProps> = ({ event }) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const openEditDialog = () => setIsEditDialogOpen(true);
  const closeEditDialog = () => setIsEditDialogOpen(false);

  return (
    <>
      <TableRow key={event.id}>
        <TableCell className="whitespace-nowrap">
          <div className="flex items-center">
            <CalendarDays className="h-4 w-4 mr-2 text-muted-foreground" />
            {format(parseISO(event.date), 'MMM d, yyyy')}
          </div>
        </TableCell>
        <TableCell>
          {event.time ? (
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
              {formatTimeTo12Hour(event.time)}
            </div>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <TransportationIcon event={event} />
            <span className="font-medium">{event.title}</span>
          </div>
        </TableCell>
        <TableCell>
          {event.location ? (
            <div className="flex items-center">
              <MapPin className="h-4 w-4 mr-1 text-muted-foreground" />
              <span>{event.location}</span>
            </div>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center">
            <User className="h-4 w-4 mr-1 text-muted-foreground" />
            <span className="text-sm">{getUserName(event)}</span>
          </div>
        </TableCell>
        <TableCell className="hidden md:table-cell">
          {event.description ? (
            <span className="text-sm font-medium">{getConfirmationNumber(event.description)}</span>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          )}
        </TableCell>
        <TableCell className="hidden md:table-cell">
          {event.description ? (
            <div className="flex items-start">
              <FileText className="h-4 w-4 mr-1 mt-0.5 text-muted-foreground" />
              <span className="text-sm">{getCleanDescription(event.description)}</span>
            </div>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          )}
        </TableCell>
        <TableCell>
          <Button
            size="sm"
            variant="ghost"
            onClick={openEditDialog}
            className="h-8 w-8 p-0"
          >
            <Pencil className="h-4 w-4" />
            <span className="sr-only">Edit</span>
          </Button>
        </TableCell>
      </TableRow>

      <EditTransportationDialog 
        open={isEditDialogOpen}
        onClose={closeEditDialog}
        event={event}
      />
    </>
  );
};
